import React, { useState, useRef, useEffect } from "react"
import ReactDOM from "react-dom"

export interface TooltipProps {
  text: string | string[] | null | undefined
  position?: "top" | "bottom" | "left" | "right"
  width?: string | number
  children: React.ReactNode
  className?: string
  usePortal?: boolean // Render tooltip in body portal — use when parent has overflow:hidden
}

const Tooltip: React.FC<TooltipProps> = ({ text, position = "top", width, children, className, usePortal = false }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLDivElement>(null)

  const tooltipPosition = {
    top: "bottom-full left-1/2 transform -translate-x-1/2 mb-3",
    bottom: "top-full left-1/2 transform -translate-x-1/2 mt-3",
    left: "right-full top-1/2 transform -translate-y-1/2 mr-3",
    right: "left-full top-1/2 transform -translate-y-1/2 ml-3"
  }

  const transformMap = {
    top:    "translate(-50%, -100%)",
    bottom: "translate(-50%, 0)",
    left:   "translate(-100%, -50%)",
    right:  "translate(0, -50%)",
  }

  useEffect(() => {
    if (!isVisible || !usePortal) return
    function onScroll() { setIsVisible(false) }
    window.addEventListener("scroll", onScroll, true)
    return () => window.removeEventListener("scroll", onScroll, true)
  }, [isVisible, usePortal])

  function computeCoords() {
    if (!triggerRef.current) return null
    const r = triggerRef.current.getBoundingClientRect()
    switch (position) {
      case "top":    return { top: r.top - 8,              left: r.left + r.width / 2 }
      case "bottom": return { top: r.bottom + 8,           left: r.left + r.width / 2 }
      case "left":   return { top: r.top + r.height / 2,   left: r.left - 8 }
      case "right":  return { top: r.top + r.height / 2,   left: r.right + 8 }
    }
  }

  const tooltipWidthStyle = typeof width === "number" ? { width: `${width}px` } : width ? { width } : {}

  const tooltipClass = "tooltip absolute bg-gray-800 text-white text-sm rounded py-2 px-3 z-50 shadow-lg min-w-[150px] max-w-xs flex items-center justify-center"
  const tooltipContent = (
    <div className="text-center whitespace-pre-line">
      {Array.isArray(text) ? text.map((line, index) => <div key={index}>{line}</div>) : text}
    </div>
  )

  return text ? (
    <div
      ref={triggerRef}
      className={`relative inline-block ${className}`}
      onMouseEnter={() => { if (usePortal) setCoords(computeCoords()); setIsVisible(true) }}
      onMouseLeave={() => setIsVisible(false)}>

      {isVisible && !usePortal && (
        <div className={`${tooltipClass} ${tooltipPosition[position]}`} style={tooltipWidthStyle}>
          <div className={`tooltip-arrow ${position}`} />
          {tooltipContent}
        </div>
      )}

      {isVisible && usePortal && coords && ReactDOM.createPortal(
        <div
          className={tooltipClass}
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            transform: transformMap[position],
            pointerEvents: "none",
            zIndex: 9999,
            ...tooltipWidthStyle,
          }}>
          <div className={`tooltip-arrow ${position}`} />
          {tooltipContent}
        </div>,
        document.body
      )}

      {children}
    </div>
  ) : (
    <>{children}</>
  )
}

export default Tooltip
